import { useEffect, useState, useCallback } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import api, { qs, apiError } from "@/lib/api";
import { monthOptions, monthLabel } from "@/lib/dpc";
import { fmtINR, fmtDay, fmtWhen } from "@/lib/service";
import { WorkshopPicker } from "@/components/WorkshopPicker";
import { TargetPanel } from "@/components/TargetPanel";
import { WarrantyToggle, WarrantyStrip, basisText, useExWarranty } from "@/components/WarrantyToggle";
import { useApp } from "@/context/AppContext";
import { canManageTargets } from "@/lib/nav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { ClipboardList, IndianRupee, Receipt, Sparkles, Package, Wrench, Users, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

const TH = "px-2.5 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap";
const rankTone = (rank, n) => (rank <= 3 ? "bg-emerald-600 text-white" : rank > n - 3 ? "bg-red-600 text-white" : "bg-secondary text-foreground");
const Rank = ({ rank, n, empty }) => (empty ? <span className="text-muted-foreground text-[10px]">—</span> : <span className={`inline-flex h-5 min-w-5 px-1 items-center justify-center rounded text-[10px] font-bold ${rankTone(rank, n)}`}>{rank}<span className="opacity-60 font-medium">/{n}</span></span>);

function Delta({ value, pct, suffix = "" }) {
  if (value == null || pct == null) return <span className="text-muted-foreground text-[10px]">no prev. data</span>;
  const Icon = value > 0 ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  const tone = value > 0 ? "text-emerald-700" : value < 0 ? "text-red-600" : "text-muted-foreground";
  return <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${tone}`}><Icon size={12} />{pct > 0 ? "+" : ""}{pct}%{suffix}</span>;
}

function Kpi({ icon: Icon, label, value, sub, testId, children }) {
  return (
    <div className="border bg-card rounded-md p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground"><Icon size={13} /> {label}</div>
      <div className="font-heading text-2xl font-extrabold tracking-tight mt-1 num">{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2">{sub}{children}</div>
    </div>
  );
}

function ClassTable({ rows, n, prevMonth, ex }) {
  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid="ws-class-table">
      <div className="px-4 py-3 border-b"><h3 className="font-heading text-sm font-semibold">Revenue per RO — GS (FS + PS + RR) · {basisText(ex)}</h3><p className="text-[11px] text-muted-foreground">Rank across all workshops · gap = what this workshop would add at the group-best rate · trend vs {monthLabel(prevMonth)}</p></div>
      <table className="w-full text-xs">
        <thead className="border-b"><tr>{["Class", "₹ / RO", "Rank", "Group", "Best", "Gap ₹", `${monthLabel(prevMonth)} ₹/RO`, "Trend"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={`border-b last:border-0 hover:bg-muted/50 ${r.key.startsWith("warranty") ? "bg-emerald-50/40" : ""}`} data-testid={`ws-class-${r.key}`}>
              <td className="px-2.5 py-1.5 font-medium whitespace-nowrap">{r.label}</td>
              <td className="px-2.5 py-1.5 num font-bold">{fmtINR(r.per_ro)}</td>
              <td className="px-2.5 py-1.5"><Rank rank={r.rank} n={n} empty={!r.best} /></td>
              <td className="px-2.5 py-1.5 num text-muted-foreground">{fmtINR(r.group_per_ro)}</td>
              <td className="px-2.5 py-1.5 num text-muted-foreground">{fmtINR(r.best)}</td>
              <td className={`px-2.5 py-1.5 num ${!r.best ? "text-muted-foreground" : r.diff_rev > 0 ? "text-red-600" : "text-emerald-700 font-semibold"}`}>{!r.best ? "—" : r.diff_rev > 0 ? fmtINR(r.diff_rev) : "best"}</td>
              <td className="px-2.5 py-1.5 num text-muted-foreground">{r.prev_per_ro ? fmtINR(r.prev_per_ro) : "—"}</td>
              <td className="px-2.5 py-1.5"><Delta value={r.delta_per_ro} pct={r.delta_pct} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CategoryTable({ rows, n, prevMonth }) {
  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid="ws-category-table">
      <div className="px-4 py-3 border-b"><h3 className="font-heading text-sm font-semibold">RO categories</h3><p className="text-[11px] text-muted-foreground">ROs per day compares this month-to-date with the full {monthLabel(prevMonth)}</p></div>
      <table className="w-full text-xs">
        <thead className="border-b"><tr>{["Category", "ROs", "RO / day", "Revenue ₹", "₹ / RO", "Rank", `${monthLabel(prevMonth)} RO/day`, `${monthLabel(prevMonth)} ₹/RO`].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={`border-b last:border-0 hover:bg-muted/50 ${r.key === "GS" ? "bg-secondary/40 font-semibold" : ""}`} data-testid={`ws-cat-${r.key}`}>
              <td className="px-2.5 py-1.5 whitespace-nowrap">{r.key} <span className="text-muted-foreground font-normal">{r.label}</span></td>
              <td className="px-2.5 py-1.5 num">{r.ro}</td>
              <td className="px-2.5 py-1.5 num">{r.ro_per_day}</td>
              <td className="px-2.5 py-1.5 num">{fmtINR(r.revenue)}</td>
              <td className="px-2.5 py-1.5 num font-bold">{fmtINR(r.per_ro)}</td>
              <td className="px-2.5 py-1.5"><Rank rank={r.rank} n={n} empty={!r.best} /></td>
              <td className="px-2.5 py-1.5 num text-muted-foreground">{r.prev_ro ? r.prev_ro_per_day : "—"}</td>
              <td className="px-2.5 py-1.5 num text-muted-foreground">{r.prev_per_ro ? fmtINR(r.prev_per_ro) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DailyChart({ daily, ex }) {
  if (!daily.length) return null;
  const data = daily.map((d) => ({ ...d, day: +d.date.slice(8) }));
  return (
    <div className="border bg-card rounded-md p-4" data-testid="ws-daily-chart">
      <h3 className="font-heading text-sm font-semibold mb-2">Daily GS revenue & ROs <span className="text-muted-foreground font-normal text-xs">· {basisText(ex)}</span></h3>
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e5e9f0" />
          <XAxis dataKey="day" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="rev" tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
          <YAxis yAxisId="ro" orientation="right" tick={{ fontSize: 10 }} />
          <Tooltip formatter={(v, name) => (name === "revenue" ? `₹${fmtINR(v)}` : name === "warranty" ? `₹${fmtINR(v)} warranty` : v)} labelFormatter={(d) => `Day ${d}`} />
          <Bar yAxisId="rev" dataKey="revenue" fill="#0f172a" radius={[2, 2, 0, 0]} />
          <Line yAxisId="ro" type="monotone" dataKey="ros" stroke="#b45309" strokeWidth={2} dot={{ r: 2 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function People({ title, rows, testId }) {
  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid={testId}>
      <div className="px-4 py-3 border-b"><h3 className="font-heading text-sm font-semibold">{title}</h3></div>
      <table className="w-full text-xs">
        <thead className="border-b"><tr>{["Name", "ROs", "Revenue ₹", "₹ / RO", "Labour/RO", "Parts/RO", "CCS/RO", "ACCS/RO"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className="border-b last:border-0 hover:bg-muted/50">
              <td className="px-2.5 py-1.5 font-medium whitespace-nowrap">{r.name}</td>
              <td className="px-2.5 py-1.5 num">{r.ros}</td>
              <td className="px-2.5 py-1.5 num">{fmtINR(r.revenue)}</td>
              <td className="px-2.5 py-1.5 num font-bold">{fmtINR(r.per_ro)}</td>
              <td className="px-2.5 py-1.5 num text-muted-foreground">{fmtINR(r.labour_per_ro)}</td>
              <td className="px-2.5 py-1.5 num text-muted-foreground">{fmtINR(r.parts_per_ro)}</td>
              <td className="px-2.5 py-1.5 num">{fmtINR(r.ccs_per_ro)}</td>
              <td className="px-2.5 py-1.5 num">{fmtINR(r.accs_per_ro)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-4 text-center text-muted-foreground">No GS invoices in this period</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function OpenSnapshot({ o, code }) {
  const B = [["d0", "< 1 day"], ["d1_3", "1–3 days"], ["d3_7", "3–7 days"], ["d7", "7+ days"]];
  return (
    <div className="border bg-card rounded-md" data-testid="ws-open-ros">
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3">
        <h3 className="font-heading text-sm font-semibold">Open repair orders</h3>
        <span className="text-[11px] text-muted-foreground">snapshot {fmtWhen(o.snapshot_at)}</span>
        <Link to={`/service/open-ros?workshop=${code}`} className="ml-auto text-xs underline font-medium" data-testid="ws-open-board-link">Open RO board →</Link>
      </div>
      <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Open</div><div className="font-heading text-xl font-extrabold num">{o.open}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Promise breached</div><div className={`font-heading text-xl font-extrabold num ${o.breached ? "text-red-600" : ""}`}>{o.breached}</div><div className="text-[10px] text-muted-foreground">{o.breached_no_reason} without a delay reason</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Aged 3+ days</div><div className={`font-heading text-xl font-extrabold num ${o.aged_3plus ? "text-amber-700" : ""}`}>{o.aged_3plus}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Estimate value</div><div className="font-heading text-xl font-extrabold num">₹{fmtINR(o.estimate_value)}</div></div>
      </div>
      <div className="px-4 pb-3 flex flex-wrap gap-1.5 text-[11px]">
        {B.map(([k, l]) => <span key={k} className="rounded bg-secondary px-2 py-0.5">{l}: <b className="num">{o.buckets[k]}</b></span>)}
        {o.stages.slice(0, 5).map((s) => <span key={s.stage} className="rounded border px-2 py-0.5 text-muted-foreground">{s.stage} <b className="num text-foreground">{s.count}</b></span>)}
      </div>
      {o.top.length > 0 && (
        <div className="overflow-auto border-t">
          <table className="w-full text-xs">
            <tbody>
              {o.top.map((r) => (
                <tr key={r.ro_no} className="border-b last:border-0">
                  <td className="px-4 py-1 font-mono whitespace-nowrap">{r.ro_no}</td>
                  <td className="px-2 py-1 whitespace-nowrap">{r.reg_no} · {r.model}</td>
                  <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">{r.stage}</td>
                  <td className="px-2 py-1 num font-semibold whitespace-nowrap">{r.age_days} d</td>
                  <td className={`px-2 py-1 whitespace-nowrap ${r.breached ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>{r.breached ? `overdue ${Math.round(r.overdue_hours)} h` : "on time"}</td>
                  <td className="px-2 py-1 text-muted-foreground truncate max-w-[200px]">{r.delay_reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Manpower({ m }) {
  return (
    <div className="border bg-card rounded-md" data-testid="ws-manpower">
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3">
        <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><Users size={14} /> Service manpower</h3>
        {m.required ? <><span className="text-xs">Required <b className="num">{m.required}</b></span><span className="text-xs">Current <b className="num">{m.current}</b></span><span className={`text-xs font-semibold ${m.gap > 0 ? "text-red-600" : "text-emerald-700"}`}>Gap {m.gap}</span><span className="text-xs text-muted-foreground ml-auto">{m.availability}% filled · as of {m.as_of?.slice(0, 10)}</span></> : <span className="text-xs text-muted-foreground">No service manpower rows for this workshop's outlets in the HR upload</span>}
      </div>
      {m.departments.length > 0 && (
        <table className="w-full text-xs">
          <thead><tr className="border-b text-[10px] uppercase tracking-wider text-muted-foreground"><th className="px-4 py-1 text-left font-semibold">Department / designation</th><th className="px-2 py-1 text-right font-semibold">Required</th><th className="px-2 py-1 text-right font-semibold">Current</th><th className="px-4 py-1 text-right font-semibold">Gap</th></tr></thead>
          <tbody>
            {m.departments.map((d) => [
              <tr key={d.dept} className="bg-muted/40 border-b"><td className="px-4 py-1.5 font-semibold">{d.dept}</td><td className="px-2 py-1.5 num text-right">{d.required}</td><td className="px-2 py-1.5 num text-right">{d.current}</td><td className={`px-4 py-1.5 num text-right font-bold ${d.gap > 0 ? "text-red-600" : "text-emerald-700"}`}>{d.gap > 0 ? `−${d.gap}` : d.gap < 0 ? `+${-d.gap}` : "0"}</td></tr>,
              ...d.rows.map((r) => <tr key={`${d.dept}-${r.designation}`} className={`border-b last:border-0 ${r.gap > 0 ? "bg-red-50/60" : ""}`}><td className="px-4 pl-7 py-1 text-muted-foreground">{r.designation}{r.remarks && <span className="ml-2 text-[10px] italic">{r.remarks}</span>}</td><td className="px-2 py-1 num text-right">{r.required}</td><td className="px-2 py-1 num text-right">{r.current}</td><td className={`px-4 py-1 num text-right ${r.gap > 0 ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>{r.gap}</td></tr>),
            ])}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function WorkshopEntry() {
  const [meta, setMeta] = useState(null);
  useEffect(() => { api.get("/service/meta").then(({ data }) => setMeta(data)).catch(() => setMeta({ workshops: [] })); }, []);
  if (!meta) return null;
  if (!meta.workshops.length) return <div className="text-sm text-muted-foreground py-20 text-center" data-testid="ws-none">No workshops in your scope — ask the admin to assign outlets to your account.</div>;
  return <Navigate to={`/service/workshop/${meta.workshops[0].code}`} replace />;
}

export default function ServiceWorkshop() {
  const { code } = useParams();
  const { user } = useApp();
  const [meta, setMeta] = useState(null);
  const [month, setMonth] = useState(monthOptions()[1]);
  const [ex, setEx] = useExWarranty();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/service/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  const load = useCallback(() => {
    setD(null);
    api.get(`/service/workshop/${code}?${qs({ month, ex_warranty: ex })}`).then(({ data }) => setD(data)).catch((e) => { toast.error(apiError(e), { id: "ws-load-error" }); setD(false); });
  }, [code, month, ex]);
  useEffect(load, [load]);

  if (d === false) return <div className="text-sm text-muted-foreground py-20 text-center">This workshop is outside your scope.</div>;
  const s = d?.summary;
  return (
    <div className="space-y-5" data-testid="service-workshop">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><h1 className="font-heading text-2xl font-extrabold tracking-tight" data-testid="ws-title">{d ? `${d.code} · ${d.name}` : code}</h1>{d?.zone && <span className="rounded bg-secondary px-2 py-0.5 text-[11px] font-semibold">{d.zone}</span>}{s && <Rank rank={s.rank} n={d.workshops} empty={!s.best_per_ro} />}</div>
          <p className="text-sm text-muted-foreground">{d ? <>Workshop dashboard · {fmtDay(d.from)} → {fmtDay(d.to)} · compared with {monthLabel(d.prev_month)} · GS {basisText(d.ex_warranty)}</> : "Loading…"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <WarrantyToggle ex={ex} onChange={setEx} />
          <WorkshopPicker workshops={meta?.workshops} value={code} allLabel="← Command Center" />
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="ws-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{monthOptions().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {s && (
        <>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-6">
            <Kpi icon={ClipboardList} label="GS ROs" value={fmtINR(s.ro)} sub={`${s.ro_per_day}/day`} testId="ws-kpi-ros"><Delta value={s.ro_per_day - s.prev_ro_per_day} pct={s.prev_ro_per_day ? Math.round(((s.ro_per_day - s.prev_ro_per_day) / s.prev_ro_per_day) * 1000) / 10 : null} suffix=" /day" /></Kpi>
            <Kpi icon={IndianRupee} label={d.ex_warranty ? "GS revenue · ex-wty" : "GS revenue"} value={`₹${fmtINR(s.revenue)}`} sub={d.ex_warranty ? `gap to best ₹${fmtINR(s.gap_rev)}` : `incl. warranty ₹${fmtINR(s.warranty?.total)} · gap to best ₹${fmtINR(s.gap_rev)}`} testId="ws-kpi-revenue" />
            <Kpi icon={Receipt} label="Revenue / RO" value={`₹${fmtINR(s.per_ro)}`} sub={`group ₹${fmtINR(s.group_per_ro)} · best ₹${fmtINR(s.best_per_ro)}`} testId="ws-kpi-per-ro"><Delta value={s.per_ro - s.prev_per_ro} pct={s.prev_per_ro ? Math.round(((s.per_ro - s.prev_per_ro) / s.prev_per_ro) * 1000) / 10 : null} /></Kpi>
            <Kpi icon={Sparkles} label="CCS / RO" value={`₹${fmtINR(s.ccs_per_ro)}`} sub="car care per GS RO" testId="ws-kpi-ccs" />
            <Kpi icon={Package} label="ACCS / RO" value={`₹${fmtINR(s.accs_per_ro)}`} sub="accessories per GS RO" testId="ws-kpi-accs" />
            <Kpi icon={Wrench} label="Open ROs" value={fmtINR(d.open_ros.open)} sub={<span className={d.open_ros.breached ? "text-red-600 font-semibold" : ""}>{d.open_ros.breached} promise breached</span>} testId="ws-kpi-open"><span className="text-[10px] uppercase tracking-wider">live</span></Kpi>
          </div>

          {d.focus.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900 flex flex-wrap gap-x-6 gap-y-1" data-testid="ws-focus">
              <b>Focus this month:</b>
              {d.focus.map((f) => <span key={f.cls}>{f.label} — ₹{fmtINR(f.per_ro)}/RO vs best ₹{fmtINR(f.best)} → <b>₹{fmtINR(f.diff_rev)}</b> on the table</span>)}
            </div>
          )}

          <WarrantyStrip w={s.warranty} meta={d.warranty_meta} perRo={s.warranty_per_ro} ex={d.ex_warranty} testId="ws-warranty-strip" />

          <TargetPanel month={month} workshop={code} myCodes={[code]} canManage={canManageTargets(user)} exWarranty={ex} />

          <div className="grid gap-4 xl:grid-cols-5 [&>*]:min-w-0">
            <div className="xl:col-span-3"><ClassTable rows={d.classes} n={d.workshops} prevMonth={d.prev_month} ex={d.ex_warranty} /></div>
            <div className="xl:col-span-2 space-y-4"><CategoryTable rows={d.categories} n={d.workshops} prevMonth={d.prev_month} /><DailyChart daily={d.daily} ex={d.ex_warranty} /></div>
          </div>
          <div className="grid gap-4 xl:grid-cols-2 [&>*]:min-w-0">
            <People title="Service advisors — GS ROs" rows={d.advisors} testId="ws-advisors" />
            <People title="Technicians — GS ROs" rows={d.technicians} testId="ws-technicians" />
          </div>
          <div className="grid gap-4 xl:grid-cols-2 [&>*]:min-w-0">
            <OpenSnapshot o={d.open_ros} code={d.code} />
            <Manpower m={d.manpower} />
          </div>
        </>
      )}
    </div>
  );
}
